import React, { createContext, useContext, useMemo } from 'react';

// Type definitions
export interface AppState {
  textSize: string;
  reduceMotion: boolean;
  language: string;
  userRole: string | null;
  currentSimulation: string | null;
  highContrast: boolean;
  darkMode: boolean;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  globalScore: number;
  // Async states for various operations
  userRoleAsync: {
    isLoading: boolean;
    error: string | null;
  };
  simulationAsync: {
    isLoading: boolean;
    error: string | null;
  };
  scoreAsync: {
    isLoading: boolean;
    error: string | null;
  };
}

export interface AppActions {
  setTextSize: (size: string) => void;
  toggleReduceMotion: () => void;
  toggleHighContrast: () => void;
  toggleDarkMode: () => void;
  toggleSound: () => void;
  toggleVibration: () => void;
  setLanguage: (lang: string) => void;
  setUserRole: (role: string | null) => void;
  setUserRoleAsync: (rolePromise: Promise<string | null>) => Promise<void>;
  setCurrentSimulation: (id: string | null) => void;
  setCurrentSimulationAsync: (simulationPromise: Promise<string | null>) => Promise<void>;
  updateGlobalScore: (score: number) => void;
  updateGlobalScoreAsync: (scorePromise: Promise<number>) => Promise<void>;
  resetState: () => void;
}

interface AppContextValue {
  state: AppState;
  actions: AppActions;
}

interface AppProviderProps {
  value: AppContextValue;
  children: React.ReactNode;
}

// --- Create context for state and actions ---
export const AppStateContext = createContext<AppState | undefined>(undefined);
export const AppActionsContext = createContext<AppActions | undefined>(undefined);

// Legacy combined context (marked for future removal)
export const AppContext = createContext<AppContextValue>({ 
  state: {} as AppState, 
  actions: {} as AppActions 
});

/**
 * AppProvider - Supplies state and actions context, and legacy context for backward compatibility.
 */
export const AppProvider: React.FC<AppProviderProps> = ({ value, children }) => (
  <AppStateContext.Provider value={value.state}>
    <AppActionsContext.Provider value={value.actions}>
      <AppContext.Provider value={value}>{children}</AppContext.Provider>
    </AppActionsContext.Provider>
  </AppStateContext.Provider>
);

/**
 * useAppStateContext - Access app state context. Throws if used outside provider.
 */
export const useAppStateContext = (): AppState => {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error(
      'useAppStateContext must be used within <AppStateContext.Provider>. ' +
        'Wrap your component in <AppProvider>.'
    );
  }
  return context;
};

/**
 * useAppActions - Access app actions context. Throws if used outside provider.
 */
export const useAppActions = (): AppActions => {
  const context = useContext(AppActionsContext);
  if (context === undefined) {
    throw new Error(
      'useAppActions must be used within <AppActionsContext.Provider>. ' +
        'Wrap your component in <AppProvider>.'
    );
  }
  return context;
};

/**
 * useAppContext - Access app context (state and actions). Throws if used outside provider.
 */
export const useAppContext = (): AppContextValue => ({
  state: useAppStateContext(),
  actions: useAppActions(),
});

/**
 * useAppSelector - Select a slice of app state, memoized. Throws if used outside provider.
 */
export function useAppSelector<T>(selector: (state: AppState) => T): T {
  const state = useAppStateContext();
  // Memoize selector output for performance, edge case: selector function identity changes
  return useMemo(() => selector(state), [selector, state]);
}
